import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { PORTAL_SUPPORT_TICKET_MAX_MESSAGE_LENGTH, PORTAL_SUPPORT_TICKET_MAX_SUBJECT_LENGTH, ServedPortalSupportTicketForm } from '@aidkitorg/types/lib/survey';
import { Alert, Button, Icon, Input, Select, Spinner, TextArea, useIsMobile, useToast } from '@aidkitorg/component-library';
import { useAPIUpload, usePost } from '../API';
import InterfaceContext from '../Context';
import { getPortalToken } from '../Hooks/PortalSession';
import { useLocalizedStrings, useLocalizer } from '../Localization';
import { useModularMarkdown } from '../Hooks/ModularMarkdown';
import { formatTime, prettyPhone } from '../Util';

// Client-only sentinel for the appended "Other" dropdown choice. Picking it
// reveals the free-text subject field; the value is never sent to the server
// (the typed subject is sent instead), so it can't collide with a real option.
const OTHER_ISSUE_TYPE = '__other__';
const DEFAULT_MAX_RECORDING_SECONDS = 120;
const DEFAULT_MAX_SCREENSHOTS = 3;
const SCREENSHOT_FILE_LIMIT = 10;
const SCREENSHOT_MAX_BYTES = 10 * 1024 * 1024;
const ACCEPTED_SCREENSHOT_TYPES = ['image/png', 'image/jpeg', 'application/pdf'];

// What /document/clean_upload returns alongside the cleaned path. Echoed back
// on submit so the saved message carries the standard attachmentMetadata map
// (the server re-validates it; it never saw the clean result itself).
type CleanedUploadMetadata = {
  cleanedPath: string,
  originalPath_DoNotOpen: string,
  fileMetadata?: any,
  pageCount?: number,
};

// A screenshot is uploaded and cleaned as soon as it's attached, so we can show a
// preview and have the cleaned savedPath ready by submit time.
type Screenshot = {
  id: number,
  file: File,
  previewUrl: string,
  isImage: boolean,
  savedPath?: string,
  metadata?: CleanedUploadMetadata,
  status: 'uploading' | 'ready' | 'error',
};

// Compact player for a just-recorded clip. Uses the measured duration (passed in)
// rather than the audio element's, which is unreliable for MediaRecorder blobs,
// and exposes only play/pause, a scrubber, and a remove (X) control.
function RecordedClip(props: { src: string, durationSeconds: number, onRemove: () => void }) {
  const L = useLocalizedStrings();
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) audio.play(); else audio.pause();
  };

  return (
    <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
      <audio
        ref={audioRef}
        src={props.src}
        className="hidden"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); setCurrentTime(0); }}
        onTimeUpdate={e => setCurrentTime(e.currentTarget.currentTime)}
      />
      <Button
        variant="unstyled"
        shape="circle"
        size="sm"
        iconPlacement="none"
        aria-label={playing ? L.applicant.support_ticket.pause_recording : L.applicant.support_ticket.play_recording}
        icon={<Icon name={playing ? 'PauseIcon' : 'PlayIcon'} />}
        onClick={togglePlay}
      />
      <input
        type="range"
        min={0}
        max={props.durationSeconds}
        step={0.1}
        value={Math.min(currentTime, props.durationSeconds)}
        onChange={e => {
          const t = Number(e.target.value);
          if (audioRef.current) audioRef.current.currentTime = t;
          setCurrentTime(t);
        }}
        aria-label={L.applicant.support_ticket.recording_progress}
        className="flex-1 accent-slate-600"
      />
      <span className="text-xs tabular-nums text-slate-600">
        {formatTime(Math.min(Math.round(currentTime), props.durationSeconds))} / {formatTime(props.durationSeconds)}
      </span>
      <Button
        variant="unstyled"
        shape="circle"
        size="sm"
        iconPlacement="none"
        aria-label={L.applicant.support_ticket.remove_recording}
        icon={<Icon name="XMarkIcon" />}
        onClick={props.onRemove}
      />
    </div>
  );
}

export function PortalSupportTicketFormComponent(props: {
  component: ServedPortalSupportTicketForm,
  portalPage: string,
  formIndex: number,
  Viewer: 'screener' | 'applicant',
}) {
  const { component } = props;
  const L = useLocalizedStrings();
  const localize = useLocalizer();
  const { lang } = useContext(InterfaceContext);
  const { toast } = useToast();

  const [view, setView] = useState<'email' | 'contact'>('email');
  const [issueType, setIssueType] = useState<string | undefined>();
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [recorded, setRecorded] = useState<{ url: string, durationSeconds: number, status: 'uploading' | 'ready' | 'error', savedPath?: string, metadata?: CleanedUploadMetadata } | null>(null);
  const [screenshots, setScreenshots] = useState<Screenshot[]>([]);
  const [dragging, setDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState<{ email?: string } | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef(0);
  const screenshotIdRef = useRef(0);
  const screenshotInputRef = useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile();

  const portalToken = { token: () => getPortalToken() || '' };
  const submitTicket = usePost('/applicant/submit_support_ticket', portalToken);
  const getUploadURL = usePost('/document/upload_url', portalToken);
  const cleanUpload = usePost('/document/clean_upload', portalToken);
  const viewDocument = usePost('/v2/document/view', portalToken);
  const upload = useAPIUpload('/upload');

  const voiceEnabled = !!component.voiceMessages;
  const maxRecordingSeconds = DEFAULT_MAX_RECORDING_SECONDS;
  const configuredMaxScreenshots = component.screenshots.maxFiles;
  const maxScreenshots = Math.min(
    configuredMaxScreenshots && configuredMaxScreenshots > 0 ? configuredMaxScreenshots : DEFAULT_MAX_SCREENSHOTS,
    SCREENSHOT_FILE_LIMIT,
  );
  const screenshotInputId = `support-ticket-screenshot-${props.formIndex}`;

  const description = useModularMarkdown({ content: component.description.content || '', info: {} });
  const confirmation = useModularMarkdown({ content: component.confirmationMessage || '', info: {} });
  const contactInstructions = useModularMarkdown({ content: component.callTextInstructions?.content || '', info: {} });

  // stop() on an already-inactive recorder throws InvalidStateError on some
  // implementations, and callers can race a stop that's already happened (the
  // timer tick fires again before onstop, unmount after a finished recording).
  const stopRecorder = () => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') recorder.stop();
  };

  // Drive both the displayed timer and the auto-stop from wall-clock time, so a
  // backgrounded/throttled tab can't let a recording run past the cap.
  useEffect(() => {
    if (!recording) return;
    const tick = () => {
      const seconds = Math.floor((Date.now() - startedAtRef.current) / 1000);
      setElapsed(seconds);
      if (seconds >= maxRecordingSeconds) stopRecorder();
    };
    const interval = window.setInterval(tick, 500);
    return () => window.clearInterval(interval);
  }, [recording, maxRecordingSeconds]);

  // Revoke the recorded blob URL when it's replaced or the form unmounts. Keyed
  // on the url, not the state object — status patches (uploading → ready) swap
  // the object but keep the url, and revoking it then would break the player.
  const recordedUrl = recorded?.url;
  useEffect(() => () => {
    if (recordedUrl) URL.revokeObjectURL(recordedUrl);
  }, [recordedUrl]);

  // Stop the recorder and release the microphone if the form unmounts mid-recording.
  useEffect(() => () => stopRecorder(), []);

  // Revoke screenshot preview URLs on unmount. Kept in a ref so this runs once,
  // independent of how the screenshots list changes during the form's life.
  const screenshotsRef = useRef(screenshots);
  screenshotsRef.current = screenshots;
  useEffect(() => () => screenshotsRef.current.forEach(s => URL.revokeObjectURL(s.previewUrl)), []);

  const discardRecording = () => {
    setRecorded(null);
    setRecording(false);
  };

  // Format US numbers as (xxx) xxx-xxxx without the +1, reusing prettyPhone for
  // the digit grouping; non-US numbers fall back to the raw value untouched. The
  // displayed text and the copied value match (WYSIWYG); only the tel: link uses
  // the raw E.164 value, since dialers handle it most reliably.
  const rawPhone = component.contact?.phone || '';
  const phoneDigits = rawPhone.replace(/\D/g, '');
  const nationalDigits = phoneDigits.length === 11 && phoneDigits.startsWith('1') ? phoneDigits.slice(1) : phoneDigits;
  const displayPhone = nationalDigits.length === 10 ? prettyPhone(nationalDigits) : rawPhone;

  const copyNumber = async () => {
    if (!displayPhone) return;
    await navigator.clipboard.writeText(displayPhone);
    toast({ variant: 'success', description: L.applicant.support_ticket.copied });
  };

  const startRecording = async () => {
    if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      toast({ variant: 'error', description: L.applicant.support_ticket.recording_not_supported });
      return;
    }
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      toast({ variant: 'error', description: L.applicant.support_ticket.microphone_permission_denied });
      return;
    }
    const mimeType = ['audio/webm', 'audio/mp4'].find(t => MediaRecorder.isTypeSupported(t));
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    chunksRef.current = [];
    recorder.ondataavailable = e => {
      if (e.data.size) chunksRef.current.push(e.data);
    };
    recorder.onstop = () => {
      recorderRef.current = null;
      stream.getTracks().forEach(track => track.stop());
      const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
      // Measure duration ourselves — MediaRecorder webm blobs report Infinity for
      // audio.duration in Chrome, so the element-derived length is unreliable.
      const durationSeconds = Math.max(1, Math.round((Date.now() - startedAtRef.current) / 1000));
      const url = URL.createObjectURL(blob);
      setRecorded({ url, durationSeconds, status: 'uploading' });
      setRecording(false);
      // Upload and clean immediately so submit is instant — and so a failure
      // surfaces now, while it's cheap to just record again.
      const fileName = blob.type.includes('mp4') ? 'voice-message.m4a' : 'voice-message.webm';
      uploadAndClean(new File([blob], fileName, { type: blob.type }))
        .then(({ savedPath, metadata }) => setRecorded(prev => (prev && prev.url === url ? { ...prev, savedPath, metadata, status: 'ready' } : prev)))
        .catch(e => {
          console.error('Error uploading voice message:', e);
          setRecorded(prev => (prev && prev.url === url ? { ...prev, status: 'error' } : prev));
          toast({ variant: 'error', description: L.applicant.support_ticket.recording_save_failed });
        });
    };
    recorder.start();
    recorderRef.current = recorder;
    startedAtRef.current = Date.now();
    setElapsed(0);
    setRecording(true);
  };

  const uploadAndClean = async (file: File) => {
    const url = await getUploadURL({ path: file.name, length: file.size });
    if (!url?.uploadURL || !url.savedPath) throw new Error(url?.error || 'upload_failed');
    await upload([file], {}, url.uploadURL, 'PUT');
    const cleaned = await cleanUpload({ savedPath: url.savedPath });
    if (!cleaned?.savedPath) throw new Error(cleaned?.validation_error || 'upload_failed');
    return { savedPath: cleaned.savedPath, metadata: 'metadata' in cleaned ? cleaned.metadata as CleanedUploadMetadata : undefined };
  };

  const updateScreenshot = (id: number, patch: Partial<Screenshot>) =>
    setScreenshots(prev => prev.map(s => (s.id === id ? { ...s, ...patch } : s)));

  const removeScreenshot = (screenshot: Screenshot) => {
    URL.revokeObjectURL(screenshot.previewUrl);
    setScreenshots(prev => prev.filter(s => s.id !== screenshot.id));
  };

  // Upload and clean each screenshot as soon as it's attached so we can preview it
  // and have the cleaned savedPath ready at submit time.
  const addScreenshots = (files: FileList | null) => {
    if (!files?.length) return;
    let slots = maxScreenshots - screenshots.length;
    const accepted: Screenshot[] = [];
    for (const file of Array.from(files)) {
      if (!ACCEPTED_SCREENSHOT_TYPES.includes(file.type)) {
        toast({ variant: 'error', description: L.applicant.support_ticket.unsupported_file_type });
        continue;
      }
      if (file.size > SCREENSHOT_MAX_BYTES) {
        toast({ variant: 'error', description: L.applicant.support_ticket.file_too_large });
        continue;
      }
      if (slots <= 0) {
        toast({ variant: 'error', description: L.applicant.support_ticket.max_files_reached });
        break;
      }
      slots--;
      const isImage = file.type.startsWith('image/');
      accepted.push({
        id: screenshotIdRef.current++,
        file,
        // Images preview instantly from the local file. PDFs show a document tile
        // until the cleaning pipeline has rebuilt their pages, then a signed
        // first-page thumbnail (fetched below once the upload is ready).
        previewUrl: isImage ? URL.createObjectURL(file) : '',
        isImage,
        status: 'uploading',
      });
    }
    if (!accepted.length) return;
    setScreenshots(prev => [...prev, ...accepted]);
    for (const screenshot of accepted) {
      uploadAndClean(screenshot.file)
        .then(async ({ savedPath, metadata }) => {
          updateScreenshot(screenshot.id, { savedPath, metadata, status: 'ready' });
          // Cleaned PDFs are a directory of page images; show the first page as
          // the thumbnail. One-shot fetch: a loaded <img> outlives the signed
          // URL's 60s expiry, and on failure the document tile just remains.
          if (!screenshot.isImage && savedPath.endsWith('/')) {
            try {
              const view = await viewDocument({ paths: [savedPath] });
              const firstPage = view?.paths?.[savedPath]?.[0];
              if (firstPage) updateScreenshot(screenshot.id, { previewUrl: firstPage });
            } catch (e) {
              console.error('Error fetching PDF thumbnail:', e);
            }
          }
        })
        .catch(e => {
          console.error('Error uploading screenshot:', e);
          updateScreenshot(screenshot.id, { status: 'error' });
          toast({ variant: 'error', description: L.applicant.support_ticket.something_went_wrong });
        });
    }
  };

  // The channel may have no issue types configured even when the form opts into
  // the dropdown; fall back to the free-text subject field in that case.
  const hasIssueTypeOptions = !!component.issueTypes?.options?.length;
  // Picking a configured option provides the subject; "Other" or no dropdown at
  // all means the applicant types one. A subject is always required.
  const realIssueSelected = hasIssueTypeOptions && !!issueType && issueType !== OTHER_ISSUE_TYPE;
  const subjectShown = !hasIssueTypeOptions || issueType === OTHER_ISSUE_TYPE;
  const subjectText = subject.trim();
  const subjectValid = realIssueSelected || (subjectShown && !!subjectText);
  const hasMessage = !!message.trim();
  const voiceUploading = recorded?.status === 'uploading';
  const voiceReady = recorded?.status === 'ready' && !!recorded.savedPath;

  const handleSubmit = async () => {
    if (!subjectValid) return;
    if (!hasMessage && !voiceReady) return;
    setSubmitting(true);
    try {
      // The recording was uploaded and cleaned when it stopped; just pass its path
      // and the clean result's metadata.
      const voiceMessage = voiceReady ? { savedPath: recorded!.savedPath!, metadata: recorded!.metadata } : undefined;
      // Screenshots were already uploaded and cleaned when attached; just pass the
      // ready ones' paths and metadata.
      const readyScreenshots = screenshots
        .filter(s => s.status === 'ready' && s.savedPath)
        .map(s => ({ savedPath: s.savedPath!, metadata: s.metadata }));
      const result = await submitTicket({
        portalPage: props.portalPage,
        formIndex: props.formIndex,
        lang,
        issueType: realIssueSelected ? issueType : undefined,
        subject: subjectShown ? subjectText : undefined,
        message: hasMessage ? message : undefined,
        voiceMessage,
        screenshots: readyScreenshots.length ? readyScreenshots : undefined,
      });
      const errorCode = result && 'error' in result ? result.error : (result ? undefined : 'request_failed');
      if (errorCode) {
        toast({
          variant: 'error',
          description: errorCode === 'no_email'
            ? L.applicant.support_ticket.no_email_error
            : L.applicant.support_ticket.something_went_wrong,
        });
        return;
      }
      setSubmitted({ email: 'email' in result ? result.email : undefined });
      setMessage('');
      setIssueType(undefined);
      setSubject('');
      screenshots.forEach(s => URL.revokeObjectURL(s.previewUrl));
      setScreenshots([]);
      discardRecording();
    } catch (e) {
      console.error('Error submitting support ticket:', e);
      toast({ variant: 'error', description: L.applicant.support_ticket.something_went_wrong });
    } finally {
      setSubmitting(false);
    }
  };

  // Configured options plus an always-present "Other" choice that reveals the
  // free-text subject field.
  const issueTypeOptions = useMemo(
    () => [
      ...(component.issueTypes?.options || []).map(t => ({ label: localize(t.label) || t.value, value: t.value })),
      { label: L.applicant.support_ticket.other, value: OTHER_ISSUE_TYPE },
    ],
    [component.issueTypes, localize, L],
  );

  const submittedAlert = submitted ? (
    <Alert variant="success" mode="block" className="mb-4">
      <div className="text-sm">
        {component.confirmationMessage
          ? confirmation
          : (submitted.email
            ? L.applicant.support_ticket.submitted_with_email.replace('$email', submitted.email)
            : L.applicant.support_ticket.submitted)}
      </div>
      <Button variant="secondary" className="mt-3" onClick={() => setSubmitted(null)}>
        {L.applicant.support_ticket.submit_another}
      </Button>
    </Alert>
  ) : null;

  // A subject is always required. When the channel has issue types the dropdown
  // supplies it (or "Other" reveals the text field); otherwise the text field
  // stands in for the dropdown.
  const issueTypeSelect = hasIssueTypeOptions ? (
    <Select
      label={L.applicant.support_ticket.issue_type}
      description={L.applicant.support_ticket.select_a_category}
      placeholder={L.applicant.support_ticket.select_a_category}
      required
      options={issueTypeOptions}
      value={issueTypeOptions.find(o => o.value === issueType)}
      onChange={option => setIssueType(option?.value)}
      labelClassName="font-bold"
    />
  ) : null;

  const subjectInput = subjectShown ? (
    <Input
      label={L.applicant.support_ticket.subject}
      showLabel
      required
      // Override the Input's default sm:max-w-sm cap so it spans the form width
      // like the issue-type Select and message field.
      wrapperClassName="sm:max-w-full"
      labelClassName="font-bold"
      value={subject}
      onChange={e => setSubject(e.target.value)}
      maxLength={PORTAL_SUPPORT_TICKET_MAX_SUBJECT_LENGTH}
    />
  ) : null;

  const screenshotsUploading = screenshots.some(s => s.status === 'uploading');
  const disabled = props.Viewer === 'screener';

  const screenshotUploader = (
    <div>
      <span className="text-sm font-bold text-slate-700">{L.applicant.support_ticket.attach_a_screenshot_optional}</span>
      <input
        ref={screenshotInputRef}
        id={screenshotInputId}
        type="file"
        accept={ACCEPTED_SCREENSHOT_TYPES.join(',')}
        multiple={maxScreenshots > 1}
        disabled={disabled}
        className="sr-only"
        onChange={e => { addScreenshots(e.target.files); e.target.value = ''; }}
      />
      {isMobile ? (
        // No drag-and-drop on touch — a tap opens the native photo library / camera / files picker.
        <div className="mt-1">
          <Button
            variant="secondary"
            icon={<Icon name="PaperClipIcon" />}
            isDisabled={disabled}
            onClick={() => screenshotInputRef.current?.click()}
          >
            {L.applicant.support_ticket.choose_a_file}
          </Button>
          <p className="mt-1 text-sm text-slate-400">{L.applicant.support_ticket.screenshot_formats}</p>
        </div>
      ) : (
        <div
          onDragOver={e => { e.preventDefault(); if (!disabled) setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => { e.preventDefault(); setDragging(false); if (!disabled) addScreenshots(e.dataTransfer.files); }}
          className={`mt-1 rounded-lg border border-dashed p-4 text-center text-sm ${dragging ? 'border-blue-400 bg-blue-50' : 'border-slate-300'}`}
        >
          <label htmlFor={screenshotInputId} className="cursor-pointer text-slate-600">
            <Icon name="PaperClipIcon" className="inline-block align-text-bottom mr-1" />
            <span className="text-blue-600 underline">{L.applicant.support_ticket.choose_a_file}</span>
            {' '}{L.applicant.support_ticket.or_drag_it_here}
          </label>
          <p className="mt-1 text-slate-400">{L.applicant.support_ticket.screenshot_formats}</p>
        </div>
      )}
      {screenshots.length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-3 list-none p-0">
          {screenshots.map(screenshot => (
            <li key={screenshot.id} className="relative w-24">
              <div className="relative h-24 w-24 rounded-lg border border-slate-200 overflow-hidden bg-slate-50 flex items-center justify-center">
                {screenshot.previewUrl ? (
                  <img src={screenshot.previewUrl} alt={screenshot.file.name} className="h-full w-full object-cover" />
                ) : (
                  <Icon name="DocumentIcon" className="text-slate-400 h-8 w-8" />
                )}
                {screenshot.status !== 'ready' && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/70">
                    {screenshot.status === 'uploading'
                      ? <Spinner size="small" />
                      : <Icon name="ExclamationTriangleIcon" className="text-red-600 h-6 w-6" role="informative" title={L.applicant.support_ticket.something_went_wrong} />}
                  </div>
                )}
                <Button
                  variant="secondary"
                  shape="circle"
                  size="sm"
                  className="!absolute top-0.5 right-0.5 !p-0.5"
                  icon={<Icon name="XMarkIcon" role="decorative" />}
                  aria-label={`${L.applicant.support_ticket.remove_attachment}: ${screenshot.file.name}`}
                  onClick={() => removeScreenshot(screenshot)}
                />
              </div>
              <span className="mt-1 block truncate text-xs text-slate-500" title={screenshot.file.name}>{screenshot.file.name}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  const voiceRecorder = (
    <div aria-live="polite" className="mt-1">
      {!recording && !recorded && (
        <Button
          variant="secondary"
          icon={<Icon name="MicrophoneIcon" />}
          isDisabled={disabled}
          onClick={startRecording}
        >
          {L.applicant.support_ticket.start_recording}
        </Button>
      )}
      {recording && (
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-2 text-sm text-slate-700">
            <span aria-hidden="true" className="h-2.5 w-2.5 rounded-full bg-red-600 animate-pulse" />
            {L.applicant.support_ticket.recording_in_progress}
            {' '}
            <span className="tabular-nums">{formatTime(elapsed)} / {formatTime(maxRecordingSeconds)}</span>
          </span>
          <Button variant="danger" onClick={stopRecorder}>
            {L.applicant.support_ticket.stop_recording}
          </Button>
        </div>
      )}
      {!recording && recorded && (
        <div className="flex flex-col gap-1">
          <RecordedClip src={recorded.url} durationSeconds={recorded.durationSeconds} onRemove={discardRecording} />
          {recorded.status === 'uploading' && (
            <span className="flex items-center gap-1.5 text-xs text-slate-500">
              <Spinner size="xsmall" /> {L.applicant.support_ticket.saving}
            </span>
          )}
          {recorded.status === 'error' && (
            <span className="text-xs text-red-600">{L.applicant.support_ticket.recording_save_failed}</span>
          )}
        </div>
      )}
    </div>
  );

  // The message is the one required part of the form: provide it in writing or
  // by voice (or both). The two inputs are separate labeled fields joined by an
  // "or" divider; issue type and screenshots stay optional around them.
  const messageInputId = `support-ticket-message-${props.formIndex}`;
  const messageHintId = `${messageInputId}-hint`;

  // A slim "or" that connects the two options rather than spacing them apart.
  const orDivider = (
    <div className="flex items-center gap-3">
      <span aria-hidden="true" className="h-px flex-1 bg-slate-200" />
      <span className="text-xs font-medium uppercase tracking-wide text-slate-400">{L.applicant.support_ticket.or}</span>
      <span aria-hidden="true" className="h-px flex-1 bg-slate-200" />
    </div>
  );

  // When voice is enabled, "Describe the issue" + helper head the whole choice
  // (the legend), and the two options below are each labeled (Type / Record).
  // When it's text-only, the textarea simply carries that label itself.
  const messageLabelId = `${messageInputId}-label`;
  const messageUnit = voiceEnabled ? (
    // role="group" + aria-labelledby instead of fieldset/legend: same grouping
    // for screen readers, but normal flow so the heading/helper spacing is
    // predictable (a native <legend> reserves its own quirky space).
    <div role="group" aria-labelledby={messageLabelId} className="rounded-lg border border-slate-200 p-4">
      <div id={messageLabelId} className="text-sm font-bold text-slate-800">
        {L.applicant.support_ticket.describe_the_issue}
        {' '}<span aria-hidden="true" className="text-red-600">*</span>
        <span className="sr-only"> ({L.component_library.textarea.required})</span>
      </div>
      <p id={messageHintId} className="mt-0.5 mb-3 text-sm text-slate-500">{L.applicant.support_ticket.describe_the_issue_hint}</p>
      <div className="flex flex-col gap-3">
        <TextArea
          id={messageInputId}
          label={L.applicant.support_ticket.type_label}
          showLabel
          value={message}
          onChange={e => setMessage(e.target.value)}
          rows={4}
          maxLength={PORTAL_SUPPORT_TICKET_MAX_MESSAGE_LENGTH}
          aria-describedby={messageHintId}
        />
        {orDivider}
        <div>
          <span className="block mb-[0.375rem] text-sm font-medium text-slate-700">{L.applicant.support_ticket.record_label}</span>
          {voiceRecorder}
        </div>
      </div>
    </div>
  ) : (
    <div>
      <label htmlFor={messageInputId} className="block mb-[0.375rem] text-sm font-bold text-slate-800">
        {L.applicant.support_ticket.describe_the_issue}
        {' '}<span aria-hidden="true" className="text-red-600">*</span>
        <span className="sr-only"> ({L.component_library.textarea.required})</span>
      </label>
      <TextArea
        id={messageInputId}
        label={L.applicant.support_ticket.describe_the_issue}
        showLabel={false}
        required
        value={message}
        onChange={e => setMessage(e.target.value)}
        rows={4}
        maxLength={PORTAL_SUPPORT_TICKET_MAX_MESSAGE_LENGTH}
        aria-describedby={messageHintId}
      />
      <p id={messageHintId} className="mt-1 text-sm text-slate-500">{L.applicant.support_ticket.describe_the_issue_hint}</p>
    </div>
  );

  const emailView = submittedAlert ?? (
    <div className="flex flex-col gap-4">
      {issueTypeSelect}
      {subjectInput}
      {messageUnit}
      {screenshotUploader}
      <div>
        <p className="mb-2 text-sm text-slate-500">{L.applicant.support_ticket.reply_to_login_email}</p>
        <Button
          onClick={handleSubmit}
          loading={submitting}
          isDisabled={
            disabled || submitting || recording || screenshotsUploading || voiceUploading
            || !subjectValid
            || (!hasMessage && !voiceReady)
          }
        >
          {L.applicant.support_ticket.submit_ticket}
        </Button>
      </div>
    </div>
  );

  // Aligns the whole callout (text, icon, and number row) together; defaults to
  // centered to match the design. textAlign covers the multi-line text blocks,
  // items-* the icon and number row.
  const callTextAlign = component.callTextInstructions?.alignment || 'center';
  const callItemsClass = callTextAlign === 'left' ? 'items-start' : callTextAlign === 'right' ? 'items-end' : 'items-center';
  const contactView = (
    <div className={`rounded-lg border border-slate-200 bg-slate-50 p-6 flex flex-col gap-3 ${callItemsClass}`} style={{ textAlign: callTextAlign }}>
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-800">
        <Icon name="PhoneIcon" className="h-6 w-6 text-white" />
      </div>
      {component.callTextInstructions?.content ? (
        // Custom text replaces the default blurb (heading included).
        <div className="text-sm text-slate-600">{contactInstructions}</div>
      ) : (
        <>
          <span className="text-base font-semibold text-slate-900">{L.applicant.support_ticket.call_or_text_line}</span>
          <p className="text-sm text-slate-600">{L.applicant.support_ticket.call_or_text_default}</p>
        </>
      )}
      <div className="flex items-center gap-2">
        <a href={`tel:${rawPhone}`} className="text-2xl font-bold text-slate-900">
          {displayPhone}
        </a>
        <Button
          variant="secondary"
          shape="circle"
          size="sm"
          iconPlacement="none"
          aria-label={L.applicant.support_ticket.copy_number}
          icon={<Icon name="ClipboardIcon" />}
          onClick={copyNumber}
        />
      </div>
    </div>
  );

  // The "Call or text us" toggle only makes sense when the channel actually has a
  // number; without one the form is the only path, so render it inline.
  const phone = component.contact?.phone;
  return (
    <div className="mb-4 flex flex-col gap-4">
      {component.description.content && <div style={{ textAlign: component.description.alignment || 'left' }}>{description}</div>}
      {phone && (
        <div className="flex flex-wrap gap-2">
          <Button
            variant={view === 'email' ? 'primary' : 'secondary'}
            icon={<Icon name="EnvelopeIcon" />}
            onClick={() => setView('email')}
            aria-pressed={view === 'email'}
          >
            {L.applicant.support_ticket.send_an_email}
          </Button>
          <Button
            variant={view === 'contact' ? 'primary' : 'secondary'}
            icon={<Icon name="PhoneIcon" />}
            onClick={() => setView('contact')}
            aria-pressed={view === 'contact'}
          >
            {L.applicant.support_ticket.call_or_text}
          </Button>
        </div>
      )}
      {phone && view === 'contact' ? contactView : emailView}
    </div>
  );
}
